/* ═════════════════════ the board, and what lands on it ═════════════════════

   Wandering thoughts: the place a half-formed thing goes so that it stops
   taking up room in your head. Three views of the same pile — the clean one
   that shows nothing but the time and an input, today's board, and the
   catalogue of everything the board has ever held.

   The reason a wandering task carries no estimate is the whole point of the
   surface. Demanding one at capture time is what stops the thing being written
   down at all, so it is weightless until it is promoted, and only then does it
   become work the planner can place. */
import { standingMultiple } from '../state/evidence.js';
import { clamp, createdToday, escapeHtml, pad, uid } from '../core/time.js';
import { touchPlan } from '../state/plans.js';
import { S, save } from '../state/store.js';
import { activeTemplate } from './appearance.js';
import { renderScheduled } from './calendar.js';
import { renderClock } from './clock.js';
import { askText, closeModal, el, openModal, reducedMotion, setDrawer, toast } from './dom.js';
import { refreshPlanningTabs } from './shell.js';
import { renderDeadlineSelects, renderList, renderStats, setFormType, showPrediction, updateEstPreview } from './tasks.js';
import { $ } from './dom.js';
import { fmtDurMin, fmtWhen } from '../core/time.js';
import { courseName } from '../state/store.js';

/** The note colours of whichever template is on. */
export const notePalette = () => activeTemplate().notes;

/** The colour that IDENTIFIES a note, for the legend swatch and the picker.
    Usually its fill — but a template may put the colour somewhere else (Swiss
    notes are all white with a coloured rule), in which case six identical
    swatches would make the colour filter unusable. */
export const swatchOf = (p) => p.swatch || p.bg;

export function paletteOf(key) {
  const pal = notePalette();
  return pal.find((p) => p.key === key) || pal[0];
}

/** Paint a note-shaped box. `--note-text` is only set where the ground needs
    it, so the default (ink) keeps coming from the stylesheet. */
export function applyNoteColor(node, colorKey) {
  const p = paletteOf(colorKey);
  node.style.setProperty('--note-bg', p.bg);
  node.style.setProperty('--note-bar', p.bar);
  node.style.setProperty('--note-line', p.line);
  if (p.text) node.style.setProperty('--note-text', p.text);
  else node.style.removeProperty('--note-text');
  return p;
}

/* ── dragging a card around the board ──
   Shared by notes and wandering-task cards. Listeners go on window, not the
   handle: pointer capture is a nice-to-have (it keeps the cursor glued to fast
   drags) but must never gate the drag. */
export function dragByHandle(node, handle, item) {
  handle.addEventListener('pointerdown', (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    const b = $('board').getBoundingClientRect();
    const dx = e.clientX - b.left - item.x;
    const dy = e.clientY - b.top - item.y;
    node.classList.add('is-dragging');
    try { handle.setPointerCapture(e.pointerId); } catch (_) { /* not fatal */ }

    const move = (ev) => {
      const bb = $('board').getBoundingClientRect();
      item.x = clamp(Math.round(ev.clientX - bb.left - dx), 0, $('board').offsetWidth - 60);
      item.y = clamp(Math.round(ev.clientY - bb.top - dy), 0, $('board').offsetHeight - 40);
      node.style.left = item.x + 'px';
      node.style.top = item.y + 'px';
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      node.classList.remove('is-dragging');
      save();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    // mouse fallback for environments that don't emit pointer moves
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  });
}

/* ── celebrate / dismiss ──
   element.animate keeps these self-contained: no library, no keyframes to keep
   in sync with the stylesheet, and the pieces clean themselves up. Both are
   skipped entirely under prefers-reduced-motion — the state change still
   happens, it just happens quietly. */

export const confettiColors = () => activeTemplate().confetti;

export function confettiBurst(node) {
  if (reducedMotion() || !node || !node.animate) return;
  const r = node.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const layer = $('fx');

  /* Offsets are in px, and the piece is pre-centred through left/top rather
     than a -50% translate: a percentage translate resolves against the
     element's own 8px box, which would keep the whole burst in a clump. */
  const colors = confettiColors();
  for (let i = 0; i < 26; i++) {
    const p = el('i', 'confetti');
    p.style.background = colors[i % colors.length];
    p.style.left = (cx - 4) + 'px';
    p.style.top = (cy - 5) + 'px';
    layer.appendChild(p);

    const angle = (Math.PI * 2 * i) / 26 + Math.random() * 0.5;
    const dist = 90 + Math.random() * 150;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist;
    const anim = p.animate([
      { transform: 'translate(0, 0) rotate(0deg)', opacity: 1, offset: 0 },
      { transform: `translate(${dx * 0.75}px, ${dy * 0.75 - 30}px) rotate(240deg)`, opacity: 1, offset: 0.45 },
      { transform: `translate(${dx}px, ${dy + 190}px) rotate(${540 * (i % 2 ? 1 : -1)}deg)`, opacity: 0, offset: 1 },
    ], { duration: 950 + Math.random() * 500, easing: 'cubic-bezier(.2,.7,.4,1)' });
    anim.onfinish = () => p.remove();
  }
}

export function puffSmoke(node) {
  if (!node) return;
  if (reducedMotion() || !node.animate) return;
  const r = node.getBoundingClientRect();
  const layer = $('fx');

  for (let i = 0; i < 12; i++) {
    const p = el('i', 'puff');
    // pre-centred in px for the same reason the confetti is
    p.style.left = (r.left + r.width * (0.15 + Math.random() * 0.7) - 15) + 'px';
    p.style.top = (r.top + r.height * (0.15 + Math.random() * 0.7) - 15) + 'px';
    layer.appendChild(p);
    const anim = p.animate([
      { transform: 'translate(0,0) scale(.5)', opacity: .6 },
      { transform: `translate(${Math.random() * 70 - 35}px, ${-40 - Math.random() * 50}px) scale(${2.4 + Math.random()})`, opacity: 0 },
    ], { duration: 700 + Math.random() * 300, easing: 'ease-out' });
    anim.onfinish = () => p.remove();
  }
  node.animate([
    { transform: 'scale(1)', opacity: 1 },
    { transform: 'scale(.7)', opacity: 0 },
  ], { duration: 380, easing: 'ease-in' });
}

export function addThought(opts = {}) {
  const wrap = $('boardWrap');
  // drop the new note near the top-left of whatever the user is currently looking at
  const t = {
    id: uid(),
    text: '',
    x: opts.x != null ? opts.x : wrap.scrollLeft + 60 + (S.thoughts.length % 6) * 26,
    y: opts.y != null ? opts.y : wrap.scrollTop + 50 + (S.thoughts.length % 6) * 22,
    w: 230, h: 150,
    color: opts.color || 'slate',
    tasks: [],
    z: nextZ(),
    created: Date.now(),
  };
  S.thoughts.push(t);
  save();
  const node = renderNote(t);
  updateThoughtChrome();
  const ta = node.querySelector('textarea');
  ta.focus();
  return t;
}

export function nextZ() {
  return S.thoughts.reduce((m, t) => Math.max(m, t.z || 1), 1) + 1;
}

/* ── wandering tasks ──
   A to-do you jot straight onto a thought, before you know what it costs.
   That is the whole point of the board: it is where half-formed things live,
   and forcing an estimate at capture time is what stops people writing them
   down at all.

   So a wandering task has no estimate, no timer, and no effect on your
   multiple — it is not a studyHub task. Promoting one hands it to the normal
   add-task form, where it picks up an estimate and the reality check like
   anything else. Nothing crosses into the arithmetic without being estimated
   first. */

/** {noteId, wtId} while the add-task form is open, so that finishing the form
 *  can tick off the wandering task it came from. @type {any} */
export let pendingPromotion = null;

/** Cleared by the task panel once the promotion has been dealt with. The board
 *  owns the binding, so only the board may assign to it. */
export function clearPendingPromotion() { pendingPromotion = null; }

export const noteById = (id) => S.thoughts.find((t) => t.id === id) || null;
export const wanderingTotal = () => S.thoughts.reduce((a, t) => a + (t.tasks || []).length, 0);

export function addWanderingTask(noteId) {
  const t = noteById(noteId);
  if (!t) return;
  askText('Wandering task', 'Something to do, before you know how long it takes', '', (text) => {
    t.tasks.push({ id: uid(), text, done: false, taskId: null });
    save(); renderNote(t); updateThoughtChrome();
  });
}

/** Redraws whichever surface the note is currently being shown on. */
export function refreshNoteSurface(t) {
  if (S.ui.boardView === 'all') renderAllTime();
  else renderNote(t);
  updateThoughtChrome();
}

export function toggleWanderingTask(noteId, wtId, fromNode) {
  const t = noteById(noteId);
  const w = t && t.tasks.find((x) => x.id === wtId);
  if (!w) return;
  w.done = !w.done;
  save();
  // ticking one off earns the same confetti as a card on the board
  if (w.done) confettiBurst(fromNode || $('board').querySelector(`[data-note="${noteId}"] .nt`));
  refreshNoteSurface(t);
}

export function removeWanderingTask(noteId, wtId) {
  const t = noteById(noteId);
  if (!t) return;
  t.tasks = t.tasks.filter((x) => x.id !== wtId);
  save(); refreshNoteSurface(t);
}

/** Hands a wandering task to the real add-task form — estimate and all. */
export function promoteWanderingTask(noteId, wtId) {
  const t = noteById(noteId);
  const w = t && t.tasks.find((x) => x.id === wtId);
  if (!w) return;
  pendingPromotion = { noteId, wtId };
  setDrawer('form');
  setFormType('task');
  renderDeadlineSelects();
  $('fTitle').value = w.text;
  $('fTitle').focus();
  updateEstPreview();
  toast('Give it an estimate — then it becomes a real task.');
}

/* ── board-level wandering tasks ──
   Same bargain as the ones on a note: caught without an estimate, so they
   never touch the multiple. Two ways out — ticked off where they stand, or
   moved into the real task list, which is where the estimate is finally
   demanded. Either way the record stays for "all time"; only the card leaves
   the board. */

// NB: the calendar already owns an isToday(Date); this one takes a timestamp
export const wanderById = (id) => S.wanderTasks.find((w) => w.id === id) || null;
export const openWanderToday = () => S.wanderTasks.filter((w) => w.state === 'open' && createdToday(w.created));

export function addWanderTaskCard(opts = {}) {
  const wrap = $('boardWrap');
  const w = {
    id: uid(),
    text: opts.text || '',
    x: opts.x != null ? opts.x : wrap.scrollLeft + 60 + (S.wanderTasks.length % 6) * 26,
    y: opts.y != null ? opts.y : wrap.scrollTop + 90 + (S.wanderTasks.length % 6) * 22,
    z: nextZ(),
    state: 'open',
    taskId: null,
    created: Date.now(),
    closedAt: null,
  };
  S.wanderTasks.push(w);
  save();
  if (S.ui.boardView === 'today') {
    const node = renderWanderCard(w);
    const ta = node && node.querySelector('textarea');
    if (ta) ta.focus();
  }
  updateThoughtChrome();
  return w;
}

export function renderWanderCard(w) {
  const board = $('board');
  const old = board.querySelector(`[data-wander="${w.id}"]`);
  if (old) old.remove();
  if (w.state !== 'open') return null;

  const n = el('div', 'note wander-card');
  n.dataset.wander = w.id;
  n.style.left = w.x + 'px';
  n.style.top = w.y + 'px';
  n.style.width = '250px';
  n.style.zIndex = w.z || 1;

  const bar = el('div', 'note-bar');
  bar.appendChild(el('span', 'note-grip', '⋮⋮'));
  bar.appendChild(el('span', 'wander-tag', 'task'));
  bar.appendChild(el('span', 'note-spacer'));
  const del = el('button', null, '✕');
  del.title = 'Delete — no record kept';
  bar.appendChild(del);
  n.appendChild(bar);

  const ta = el('textarea');
  ta.value = w.text;
  ta.placeholder = 'something to do…';
  ta.addEventListener('input', () => { w.text = ta.value; save(); });
  n.appendChild(ta);

  const acts = el('div', 'wander-acts');
  const doneBtn = el('button', 'btn btn-primary', '✓ Completed');
  doneBtn.title = 'Tick it off where it stands';
  doneBtn.addEventListener('click', (e) => { e.stopPropagation(); completeWanderTask(w.id); });
  const moveBtn = el('button', 'btn', '↗ Move to task list');
  moveBtn.title = 'Give it a group and an estimate — it becomes a real task';
  moveBtn.addEventListener('click', (e) => { e.stopPropagation(); openMoveToTasks(w.id); });
  acts.append(doneBtn, moveBtn);
  n.appendChild(acts);

  board.appendChild(n);

  const raise = () => { w.z = nextZ(); n.style.zIndex = w.z; save(); };
  n.addEventListener('pointerdown', raise, true);
  dragByHandle(n, bar, w);

  del.addEventListener('click', (e) => {
    e.stopPropagation();
    S.wanderTasks = S.wanderTasks.filter((x) => x.id !== w.id);
    save(); n.remove(); updateThoughtChrome();
  });

  return n;
}

/** Removes a card once its send-off animation has played. */
export function retireWanderCard(id, delay) {
  const node = $('board').querySelector(`[data-wander="${id}"]`);
  if (!node) { updateThoughtChrome(); return; }
  setTimeout(() => { node.remove(); updateThoughtChrome(); }, reducedMotion() ? 0 : delay);
}

/** Ticking off works from the board card or from its block in the archive. */
export function completeWanderTask(id, fromNode) {
  const w = wanderById(id);
  if (!w || w.state !== 'open') return;
  w.state = 'done';
  w.closedAt = Date.now();
  save();

  confettiBurst(fromNode || $('board').querySelector(`[data-wander="${id}"]`));

  if (S.ui.boardView === 'all') {
    // the block stays put and turns into its completed self
    renderAllTime();
    updateThoughtChrome();
    toast('Done.');
  } else {
    retireWanderCard(id, 620);
    toast('Done. It is kept under All time.');
  }
}

export let moveId = null;

export function moveEstMin() { return (+$('mvEstH').value || 0) * 60 + (+$('mvEstM').value || 0); }

export function openMoveToTasks(id) {
  const w = wanderById(id);
  if (!w) return;
  if (!w.text.trim()) return toast('Give it a name first.', true);
  moveId = id;

  $('mvTitle').value = w.text.trim();
  const sel = $('mvCourse');
  sel.textContent = '';
  S.courses.forEach((c) => sel.appendChild(new Option(c.name, c.id)));
  sel.value = (S.courses[0] || {}).id || '';

  const dsel = $('mvDeadline');
  dsel.textContent = '';
  dsel.appendChild(new Option('— none —', ''));
  S.deadlines.slice().sort((a, b) => a.due - b.due)
    .forEach((d) => dsel.appendChild(new Option(`${d.title} · ${fmtWhen(d.due)}`, d.id)));
  dsel.value = '';

  updateMoveMeta();
  openModal('moveModal');
  setTimeout(() => $('mvTitle').focus(), 30);
}

export function updateMoveMeta() {
  const est = moveEstMin();
  const m = standingMultiple();
  $('mvMeta').innerHTML = est > 0
    ? escapeHtml(`estimate ${fmtDurMin(est)} × ${(m || 1).toFixed(2)} = ${fmtDurMin(est * (m || 1))} projected`)
    : 'Give it an estimate — that is what makes it a task.';
}

export function confirmMove() {
  const w = wanderById(moveId);
  if (!w) return;
  const title = $('mvTitle').value.trim();
  if (!title) return toast('The task needs a title.', true);
  const est = moveEstMin();
  if (est <= 0) return toast('Give the task a time estimate.', true);

  const t = {
    id: uid(), kind: 'task', title, courseId: $('mvCourse').value,
    estMin: est,
    accSec: 0, runSince: null,
    done: false, multiple: null,
    actualMin: null,
    subtasks: [],
    deadlineId: $('mvDeadline').value || null,
    start: null,
    created: Date.now(), completedAt: null,
    fromWandering: true,
  };
  S.tasks.push(t);

  w.state = 'moved';
  w.taskId = t.id;
  w.closedAt = Date.now();
  // it was a line on the board with no cost; it is work the week owes now
  touchPlan();
  save();

  closeModal('moveModal');
  if (S.ui.boardView === 'all') {
    // no board card to send off here — puff the archive block, then redraw it
    puffSmoke($('allTimeView').querySelector(`[data-at-task="${w.id}"]`));
    setTimeout(() => { renderAllTime(); updateThoughtChrome(); }, reducedMotion() ? 0 : 400);
  } else {
    puffSmoke($('board').querySelector(`[data-wander="${w.id}"]`));
    retireWanderCard(w.id, 400);
  }
  renderList(); renderStats(); renderScheduled(); renderClock();
  refreshPlanningTabs();
  showPrediction(est, {});
  toast(`Moved into ${courseName(t.courseId)}`);
}

export function noteTasksBlock(t) {
  const wrap = el('div', 'note-tasks');
  const list = t.tasks || [];
  if (!list.length) { wrap.hidden = true; return wrap; }

  list.forEach((w) => {
    const row = el('div', 'nt' + (w.done ? ' is-done' : '') + (w.taskId ? ' is-promoted' : ''));

    const box = el('input', 'nt-check');
    box.type = 'checkbox';
    box.checked = w.done;
    box.addEventListener('click', (e) => e.stopPropagation());
    box.addEventListener('change', () => toggleWanderingTask(t.id, w.id));
    row.appendChild(box);

    row.appendChild(el('span', 'nt-text', w.text));

    if (w.taskId) {
      // a glyph here would read as a second checkbox — say it in words
      const mark = el('span', 'nt-mark', 'in tasks');
      mark.title = 'Already promoted to a studyHub task';
      row.appendChild(mark);
    } else {
      const up = el('button', 'nt-btn', '↗');
      up.title = 'Make this a real task (it will need an estimate)';
      up.addEventListener('click', (e) => { e.stopPropagation(); promoteWanderingTask(t.id, w.id); });
      row.appendChild(up);
    }

    const x = el('button', 'nt-btn nt-x', '✕');
    x.title = 'Delete';
    x.addEventListener('click', (e) => { e.stopPropagation(); removeWanderingTask(t.id, w.id); });
    row.appendChild(x);

    wrap.appendChild(row);
  });
  return wrap;
}

export function renderNote(t) {
  const board = $('board');
  const old = board.querySelector(`[data-note="${t.id}"]`);
  if (old) old.remove();

  const n = el('div', 'note');
  n.dataset.note = t.id;
  n.style.left = t.x + 'px';
  n.style.top = t.y + 'px';
  n.style.width = t.w + 'px';
  n.style.height = t.h + 'px';
  n.style.zIndex = t.z || 1;
  applyNoteColor(n, t.color);

  const bar = el('div', 'note-bar');
  bar.appendChild(el('span', 'note-grip', '⋮⋮'));
  bar.appendChild(el('span', 'note-spacer'));

  const taskBtn  = el('button', null, '☑'); taskBtn.title = 'Add a wandering task under this thought';
  const paintBtn = el('button', null, '◐'); paintBtn.title = 'Colour — group notes by colour';
  const dupBtn   = el('button', null, '⧉'); dupBtn.title = 'Duplicate';
  const delBtn   = el('button', null, '✕'); delBtn.title = 'Delete note';
  bar.append(taskBtn, paintBtn, dupBtn, delBtn);
  n.appendChild(bar);

  const ta = el('textarea');
  ta.value = t.text;
  ta.placeholder = 'a wandering thought…';
  ta.addEventListener('input', () => { t.text = ta.value; save(); });
  n.appendChild(ta);

  n.appendChild(noteTasksBlock(t));
  taskBtn.addEventListener('click', (e) => { e.stopPropagation(); addWanderingTask(t.id); });

  const grip = el('div', 'note-resize');
  grip.title = 'Resize';
  n.appendChild(grip);

  board.appendChild(n);

  /* ── bring to front ── */
  const raise = () => { t.z = nextZ(); n.style.zIndex = t.z; save(); };
  n.addEventListener('pointerdown', raise, true);

  dragByHandle(n, bar, t);

  /* ── resize ── */
  grip.addEventListener('pointerdown', (e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sy = e.clientY, sw = t.w, sh = t.h;
    try { grip.setPointerCapture(e.pointerId); } catch (_) { /* not fatal */ }
    const move = (ev) => {
      t.w = clamp(Math.round(sw + ev.clientX - sx), 150, 900);
      t.h = clamp(Math.round(sh + ev.clientY - sy), 110, 800);
      n.style.width = t.w + 'px';
      n.style.height = t.h + 'px';
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      save();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  });

  /* ── colour picker ── */
  paintBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const existing = n.querySelector('.note-palette');
    if (existing) return existing.remove();
    const pal = el('div', 'note-palette');
    notePalette().forEach((c) => {
      const b = el('button');
      b.style.background = swatchOf(c);
      b.title = c.key;
      b.addEventListener('click', (ev) => {
        ev.stopPropagation();
        t.color = c.key; save();
        renderNote(t); updateThoughtChrome();
      });
      pal.appendChild(b);
    });
    n.appendChild(pal);
    const away = (ev) => {
      if (!pal.contains(ev.target) && ev.target !== paintBtn) { pal.remove(); document.removeEventListener('pointerdown', away); }
    };
    setTimeout(() => document.addEventListener('pointerdown', away), 0);
  });

  /* ── duplicate / delete ── */
  dupBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const copy = { ...t, id: uid(), x: t.x + 24, y: t.y + 24, z: nextZ(), created: Date.now() };
    S.thoughts.push(copy); save(); renderNote(copy); updateThoughtChrome();
  });
  delBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    S.thoughts = S.thoughts.filter((x) => x.id !== t.id);
    save(); n.remove(); updateThoughtChrome();
  });

  applyNoteFilter(n, t);
  return n;
}

export function applyNoteFilter(node, t) {
  const f = S.ui.noteColorFilter;
  const dim = f && t.color !== f;
  node.style.opacity = dim ? 0.22 : 1;
  node.style.filter = dim ? 'saturate(.4)' : 'none';
}

/* ── the three ways to look at this board ──
   clean  · nothing but the time and a place to put what interrupted you
   today  · the board itself, holding what turned up today
   all    · the archive, including what was ticked off or moved away */

export function renderThoughts() {
  $('board').textContent = '';
  S.thoughts.filter((t) => createdToday(t.created)).forEach(renderNote);
  openWanderToday().forEach(renderWanderCard);
  updateThoughtChrome();
}

export function setBoardView(v) {
  S.ui.boardView = ['clean', 'today', 'all'].includes(v) ? v : 'today';
  save();
  renderBoardView();
}

export function renderBoardView() {
  const v = S.ui.boardView;
  [...$('boardViewSeg').children].forEach((b) => b.classList.toggle('is-active', b.dataset.view === v));

  $('boardWrap').hidden = v !== 'today';
  $('cleanView').hidden = v !== 'clean';
  $('allTimeView').hidden = v !== 'all';

  /* the board's own chrome only means anything on the board */
  const boardOnly = v === 'today';
  ['addThought', 'addWanderTask', 'tidyThoughts', 'clearThoughts'].forEach((id) => { $(id).hidden = !boardOnly; });
  $('swatchLegend').hidden = !boardOnly;
  $('boardHint').textContent = v === 'today'
    ? 'Drag a note by its top bar to group it. Notes autosave.'
    : v === 'clean'
      ? 'Write it down, let it go.'
      : 'Everything this board has ever held.';

  if (v === 'today') renderThoughts();
  if (v === 'all') renderAllTime();
  if (v === 'clean') { renderCleanClock(); $('cleanInput').focus(); }
  updateThoughtChrome();
}

export function renderCleanClock() {
  if ($('cleanView').hidden) return;
  const now = new Date();
  let h = now.getHours();
  const m = pad(now.getMinutes());
  let ampm = '';
  // `hh` rather than reassigning `h`: one branch wants a number and the other
  // a zero-padded string, and only the text matters from here on.
  let hh = String(h);
  if (S.clock.format === '12') { ampm = h >= 12 ? ' PM' : ' AM'; hh = String(h % 12 || 12); } else { hh = pad(h); }
  $('cleanTime').textContent = `${hh}:${m}${ampm}`;
  $('cleanDate').textContent = now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
}

/** Capture from the clean view: it is saved, and it leaves the screen at once. */
export function cleanCapture(kind) {
  const text = $('cleanInput').value.trim();
  if (!text) return;
  if (kind === 'task') addWanderTaskCard({ text });
  else {
    const t = addThought({});
    t.text = text;
    save();
    const node = $('board').querySelector(`[data-note="${t.id}"]`);
    if (node) node.remove();          // never shown here — that is the point
  }
  $('cleanInput').value = '';
  $('cleanInput').focus();
  const note = $('cleanNote');
  note.textContent = kind === 'task' ? 'Kept as a task. Gone from here.' : 'Kept as a thought. Gone from here.';
  note.classList.add('is-flash');
  setTimeout(() => {
    note.classList.remove('is-flash');
    note.textContent = 'It leaves the screen the moment you write it down. Today has it when you want it.';
  }, 1800);
}

/* ── the archive, as a catalogue ──
   A day's cards keep the arrangement they were given on the board: notes hold
   their colour (which is how they were grouped in the first place), and within
   a day everything is laid out in the reading order of where it sat — banded
   by row, then left to right. The point is that a day still looks like the day
   it was, rather than being flattened into a log. */

export const boardOrder = (ref) => Math.round((ref.y || 0) / 140) * 100000 + (ref.x || 0);

export function renderAllTime() {
  const wrap = $('allTimeView');
  wrap.textContent = '';

  const entries = [
    ...S.thoughts.map((t) => ({ kind: 'thought', at: t.created, ref: t })),
    ...S.wanderTasks.map((w) => ({ kind: 'task', at: w.created, ref: w })),
  ];

  if (!entries.length) {
    wrap.appendChild(el('div', 'empty-list', 'Nothing has passed through this board yet.'));
    return;
  }

  /* bucket by calendar day, newest day first */
  const days = new Map();
  entries.forEach((e) => {
    const d = new Date(e.at); d.setHours(0, 0, 0, 0);
    const key = d.getTime();
    if (!days.has(key)) days.set(key, []);
    days.get(key).push(e);
  });

  [...days.keys()].sort((a, b) => b - a).forEach((key) => {
    const h = el('div', 'at-day');
    h.appendChild(el('span', null, new Date(key).toLocaleDateString(undefined,
      { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })));
    const items = days.get(key);
    h.appendChild(el('span', 'at-day-count', createdToday(key) ? 'today' : `${items.length} card${items.length === 1 ? '' : 's'}`));
    wrap.appendChild(h);

    const grid = el('div', 'at-grid');
    items
      .sort((a, b) => boardOrder(a.ref) - boardOrder(b.ref) || a.at - b.at)
      .forEach((e) => grid.appendChild(e.kind === 'task' ? allTimeTaskBox(e.ref) : allTimeNoteBox(e.ref)));
    wrap.appendChild(grid);
  });
}

/** Autosizing editable text. Never redraws on input — that would eat the caret. */
export function archiveTextarea(initial, onInput, placeholder) {
  const ta = el('textarea', 'at-box-text');
  ta.value = initial || '';
  ta.rows = 1;
  ta.placeholder = placeholder || '';
  const grow = () => { ta.style.height = 'auto'; ta.style.height = ta.scrollHeight + 'px'; };
  ta.addEventListener('input', () => { onInput(ta.value); save(); grow(); });
  setTimeout(grow, 0);
  return ta;
}

/** A note in the catalogue: same colour it had on the board, still editable. */
export function allTimeNoteBox(t) {
  const box = el('div', 'at-box is-note');
  box.dataset.atNote = t.id;
  applyNoteColor(box, t.color);

  const head = el('div', 'at-box-head');
  head.appendChild(el('span', 'at-box-kind', 'thought'));
  head.appendChild(el('span', 'spacer'));
  head.appendChild(el('span', 'at-box-when', stampOf(t.created)));
  const del = el('button', 'at-box-x', '✕');
  del.title = 'Delete this note for good';
  del.addEventListener('click', () => {
    S.thoughts = S.thoughts.filter((x) => x.id !== t.id);
    save();
    const onBoard = $('board').querySelector(`[data-note="${t.id}"]`);
    if (onBoard) onBoard.remove();
    renderAllTime(); updateThoughtChrome();
  });
  head.appendChild(del);
  box.appendChild(head);

  box.appendChild(archiveTextarea(t.text, (v) => { t.text = v; }, 'an empty thought…'));

  const tasks = t.tasks || [];
  if (tasks.length) {
    const list = el('div', 'at-box-subs');
    tasks.forEach((w) => {
      const row = el('label', 'nt' + (w.done ? ' is-done' : '') + (w.taskId ? ' is-promoted' : ''));
      const cb = el('input', 'nt-check');
      cb.type = 'checkbox';
      cb.checked = w.done;
      cb.addEventListener('change', () => toggleWanderingTask(t.id, w.id, row));
      row.appendChild(cb);
      row.appendChild(el('span', 'nt-text', w.text));
      if (w.taskId) row.appendChild(el('span', 'nt-mark', 'in tasks'));
      const x = el('button', 'nt-btn nt-x', '✕');
      x.title = 'Delete';
      x.addEventListener('click', (e) => { e.preventDefault(); removeWanderingTask(t.id, w.id); });
      row.appendChild(x);
      list.appendChild(row);
    });
    box.appendChild(list);
  }
  return box;
}

export const stampOf = (ms) => new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
export const dayStampOf = (ms) =>
  new Date(ms).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** A wandering task in the catalogue: still a live thing, not a log line. */
export function allTimeTaskBox(w) {
  const box = el('div', 'at-box is-task is-' + w.state);
  box.dataset.atTask = w.id;

  const head = el('div', 'at-box-head');
  const badge = el('span', 'badge at-state is-' + w.state,
    w.state === 'done' ? 'completed' : w.state === 'moved' ? 'moved to tasks' : 'open');
  if (w.state === 'moved' && w.taskId) {
    const real = S.tasks.find((t) => t.id === w.taskId);
    badge.title = real ? `Now a task: ${real.title}` : 'The task it became has since been deleted.';
  }
  head.appendChild(badge);
  head.appendChild(el('span', 'spacer'));
  head.appendChild(el('span', 'at-box-when', stampOf(w.created)));
  const del = el('button', 'at-box-x', '✕');
  del.title = 'Remove it from the archive for good';
  del.addEventListener('click', () => deleteWanderTask(w.id));
  head.appendChild(del);
  box.appendChild(head);

  box.appendChild(archiveTextarea(w.text, (v) => { w.text = v; }, 'something to do…'));

  if (w.state === 'done' && w.closedAt) {
    box.appendChild(el('div', 'at-closed', `completed ${dayStampOf(w.closedAt)}`));
  } else if (w.state === 'moved' && w.closedAt) {
    box.appendChild(el('div', 'at-closed', `moved to the task list ${dayStampOf(w.closedAt)}`));
  }

  if (w.state === 'open') {
    const acts = el('div', 'at-box-acts');
    const done = el('button', 'btn btn-primary', '✓ Completed');
    done.addEventListener('click', () => completeWanderTask(w.id, box));
    acts.appendChild(done);
    const move = el('button', 'btn', '↗ Move');
    move.title = 'Give it a group and an estimate — it becomes a real task';
    move.addEventListener('click', () => openMoveToTasks(w.id));
    acts.appendChild(move);
    box.appendChild(acts);
  }
  return box;
}

export function deleteWanderTask(id) {
  S.wanderTasks = S.wanderTasks.filter((w) => w.id !== id);
  save();
  const onBoard = $('board').querySelector(`[data-wander="${id}"]`);
  if (onBoard) onBoard.remove();
  if (S.ui.boardView === 'all') renderAllTime();
  updateThoughtChrome();
}

export function updateThoughtChrome() {
  const allView = S.ui.boardView === 'all';
  // what is on the board right now — also decides whether the empty hint shows
  const n = allView ? S.thoughts.length : S.thoughts.filter((t) => createdToday(t.created)).length;
  const cards = allView ? S.wanderTasks.length : openWanderToday().length;
  const w = cards + wanderingTotal();

  $('thoughtCount').textContent = allView
    ? `${n} note${n === 1 ? '' : 's'} · ${w} task${w === 1 ? '' : 's'} ever`
    : `${n} note${n === 1 ? '' : 's'} today` + (w ? ` · ${w} wandering task${w === 1 ? '' : 's'}` : '');

  // colour legend doubles as a filter
  const leg = $('swatchLegend');
  leg.textContent = '';
  const counts = {};
  S.thoughts.forEach((t) => { counts[t.color] = (counts[t.color] || 0) + 1; });
  notePalette().forEach((c) => {
    if (!counts[c.key] && S.ui.noteColorFilter !== c.key) return;
    const b = el('button');
    b.style.background = swatchOf(c);
    b.style.borderColor = S.ui.noteColorFilter === c.key ? 'var(--text)' : 'transparent';
    if (S.ui.noteColorFilter === c.key) b.classList.add('is-active');
    b.title = `${counts[c.key] || 0} ${c.key} note(s) — click to isolate`;
    b.addEventListener('click', () => {
      S.ui.noteColorFilter = S.ui.noteColorFilter === c.key ? '' : c.key;
      save();
      S.thoughts.forEach((t) => {
        const node = $('board').querySelector(`[data-note="${t.id}"]`);
        if (node) applyNoteFilter(node, t);
      });
      updateThoughtChrome();
    });
    leg.appendChild(b);
  });

  let empty = $('board').querySelector('.board-empty');
  if (!n && !cards) {
    if (!empty) {
      empty = el('div', 'board-empty', 'Press ＋ New thought or ＋ New task. Drag cards by their top bar to cluster related ideas.');
      $('board').appendChild(empty);
    }
  } else if (empty) empty.remove();
}

export function initThoughtsTab() {
  $('addThought').addEventListener('click', () => addThought());
  $('addWanderTask').addEventListener('click', () => addWanderTaskCard());

  [...$('boardViewSeg').children].forEach((b) =>
    b.addEventListener('click', () => setBoardView(b.dataset.view)));

  $('cleanForm').addEventListener('submit', (e) => { e.preventDefault(); cleanCapture('thought'); });
  $('cleanKeepTask').addEventListener('click', () => cleanCapture('task'));

  $('mvConfirm').addEventListener('click', confirmMove);
  ['mvEstH', 'mvEstM'].forEach((id) => $(id).addEventListener('input', updateMoveMeta));

  $('board').addEventListener('dblclick', (e) => {
    if (e.target !== $('board')) return;
    const b = $('board').getBoundingClientRect();
    addThought({ x: Math.round(e.clientX - b.left - 20), y: Math.round(e.clientY - b.top - 13) });
  });

  /* Clears what is on the board today. Anything already ticked off or moved
     stays in All time — clearing the desk is not the same as erasing the day. */
  $('clearThoughts').addEventListener('click', () => {
    const notes = S.thoughts.filter((t) => createdToday(t.created));
    const cards = openWanderToday();
    const n = notes.length + cards.length;
    if (!n) return;
    askText('Clear the board', `Type DELETE to remove today's ${n} card(s)`, '', (v) => {
      if (v.toUpperCase() !== 'DELETE') return toast('Not cleared.');
      const noteIds = notes.map((t) => t.id);
      const cardIds = cards.map((w) => w.id);
      S.thoughts = S.thoughts.filter((t) => !noteIds.includes(t.id));
      S.wanderTasks = S.wanderTasks.filter((w) => !cardIds.includes(w.id));
      save(); renderThoughts(); toast('Board cleared');
    });
  });

  $('tidyThoughts').addEventListener('click', () => {
    const perRow = Math.max(1, Math.floor(($('boardWrap').clientWidth - 40) / 250));
    // task cards first, then notes by colour group
    openWanderToday().forEach((w, i) => {
      w.x = 24 + (i % perRow) * 250;
      w.y = 24 + Math.floor(i / perRow) * 175;
    });
    const rows = Math.ceil(openWanderToday().length / perRow);
    // keep colour groups together when tidying
    const order = [...S.thoughts].filter((t) => createdToday(t.created)).sort((a, b) =>
      notePalette().findIndex((p) => p.key === a.color) - notePalette().findIndex((p) => p.key === b.color)
      || a.created - b.created);
    order.forEach((t, i) => {
      t.x = 24 + (i % perRow) * 250;
      t.y = 24 + (rows + Math.floor(i / perRow)) * 175;
    });
    save(); renderThoughts(); toast('Board tidied');
  });
}
